import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { IDragFileBrowseData, IReturnChangesWidgetEditForm, checkIfFieldDisabled, languagesList, languagesList_, widgetEditMode } from 'na-lib';
import { CommentsService } from 'src/app/service/comments.service';
import { SettingsService } from 'src/app/service/settings.service';
import { UploadService } from 'src/app/service/upload.service';
import { supportUrlStructure } from 'src/app/supportUrls';
import { checkUrlProtocol, urlInvalidCharactersValidator } from 'src/app/validators/formValidators.dt';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

type LinkChip =
  | { kind: 'child'; id: string; label: string }
  | { kind: 'anchor'; id: string; label: string };

interface AnchorOption {
  id: string;
  label?: string;
  name?: string;
}
interface ChildPageOption {
  id: string;
  name?: string;
}

@Component({
    selector: 'app-image-edit-form',
    templateUrl: './image-edit-form.component.html',
    styleUrl: './image-edit-form.component.css',
    standalone: false
})

export class ImageEditFormComponent {
  @Input() data:widgetEditMode
  @Output() returnChanges = new EventEmitter<IReturnChangesWidgetEditForm>()
  @Output() formChange = new EventEmitter<FormGroup>();
  urlRegex = /^(?:\$\{[A-Za-z0-9._:-]+\}|#\S+|child:[^\s]+|dpp:\/\/main|(?:https?:\/\/|context:\/\/|pass:\/\/|content:\/\/|task:\/\/|tridy:\/\/)\S+)$/;
  urlRegexDoc = /^(?:\$\{[^}]+\}|pass:\/\/[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+)$/;
  urlRegexDynamic = /^\$\{[A-Za-z0-9._:-]+\}$/;

  languagesList = languagesList
  imageData:any
  oldData:any
  defaultLanguage:string
  widgetNumber:any
  passId:any
  url:any
  translationModeOriginal = false
  dragFileData: IDragFileBrowseData
  imageEditForm!:FormGroup
  urlData:any
  addUrl = false
  dynamicUrl = false
  userHasBetaWidgetRole = false
  changes=false
  @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
  noUrl=false
  linkUrlTransform:any
  newImage:any
  disableFilters = false
  anchorOptions: AnchorOption[] = [];
  childPages: ChildPageOption[] = [];
  linkChip: LinkChip | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    public dialog: MatDialog,
    private uploadService: UploadService,
    private commentService: CommentsService,
    private translate:TranslateService,
    private settingsService: SettingsService
  ){
  // dialogRef.disableClose = true;
    const currentLang = this.translate.currentLang() ?? 'en';
    this.languagesList = languagesList_[currentLang]
  }

  ngOnInit():void{
    if(this.settingsService.checkIfBetaRoleAccess())
    this.userHasBetaWidgetRole = true

    this.defaultLanguage = this.data.defaultLanguage
    this.imageData=this.data.values
    this.oldData=this.imageData
    this.url=this.oldData.url
    this.passId = this.data.passId
    this.widgetNumber= this.data.widget

    if(this.data.translateMode){
      this.disableFilters = true
      let translateData = this.data.translateData
      const {fields, originalLanguageData, defaultLanguageWidgetData, valuesTranslated, selectedLanguage, values} = translateData

      if(translateData.originalLanguageData){
        this.translationModeOriginal = true
        this.dragFileData = translateData.dragFileDataOriginal
        this.initTranslateEditData( defaultLanguageWidgetData, valuesTranslated, originalLanguageData, [],selectedLanguage)
      }else{
        this.translationModeOriginal = false

        this.dragFileData = translateData.dragFileData
        this.initTranslateEditData(values,valuesTranslated, originalLanguageData, fields, selectedLanguage)
      }
    }
    else{
      this.dragFileData = {
        calledFrom: ImageEditFormComponent,
        calledFromData:{
          passId: this.passId,
          widgetNumber:this.widgetNumber,
          widget: "image",
        },
        enableDragDrop: true,
        enableBrowse:true,
        enableMedia:true,
        enableEnterUrl:true,
        enableMultipleDragDrop:false,
        supportedFileTypes:['image/jpeg', 'image/png', 'image/webp'],
        supportedFileAccept:".jpg, .jpeg, .png, .webp",
        maxSizeInBytes: 2 * 1024 * 1024, // 2 MB size limit for compression
        maxAllowedSizeInBytes:20 * 1024 * 1024, // 20 MB size limit for direct upload,
        returnSupportUrl: undefined,
        returnUrlData: undefined,
        enterUrl:false,
        returnUrlType:undefined
      }
      this.initWidgetEdit()
    }

    const extra = this.data as any;
    this.anchorOptions = extra.anchorOptions || [];
    this.childPages   = extra.childPages || [];
    const ctrl = this.imageEditForm?.get('linkURL');

    if (ctrl) {
      this.linkChip = this.makeLinkChip(ctrl.value);
      ctrl.valueChanges.subscribe(v => {
        this.linkChip = this.makeLinkChip(v);
      });
    }
      
  }

  initTranslateEditData(data, valuesTranslated, originalLanguageData, fields, selectedLanguage){
  
    let {id, type, source, copyright, position, size, name , title, subtitle, url, linkURL, altTitle, filter, fallbackUrl, widgetStyle} = data

    if(!originalLanguageData){
      title = valuesTranslated[selectedLanguage +'.'+id+'.title']
      subtitle = valuesTranslated[selectedLanguage +'.'+id+'.subtitle']
      url = valuesTranslated[selectedLanguage +'.'+id+'.url']
      linkURL = valuesTranslated[selectedLanguage +'.'+id+'.linkURL']
      altTitle = valuesTranslated[selectedLanguage +'.'+id+'.altTitle']
    }

    this.urlData = supportUrlStructure(url ?? this.url, this.passId, this.settingsService.contextId$)
    this.imageEditForm = this.fb.group({
      id:[{value:id ? id : undefined,  disabled: checkIfFieldDisabled('id', fields, originalLanguageData)}],
      type:[{value: type ? type : undefined,  disabled: checkIfFieldDisabled('type', fields, originalLanguageData)}],
      url:[{value:url ? url : undefined, disabled: checkIfFieldDisabled('url', fields, originalLanguageData)}, [ checkUrlProtocol, urlInvalidCharactersValidator()]],
      linkURL:[{value:linkURL ? linkURL : undefined, disabled: checkIfFieldDisabled('linkURL', fields, originalLanguageData)}, [checkUrlProtocol, urlInvalidCharactersValidator()]],
      source: [{value:source ? source : undefined,  disabled: checkIfFieldDisabled('source', fields, originalLanguageData)}],
      copyright: [{value:copyright ? copyright : undefined,  disabled: checkIfFieldDisabled('copyright', fields, originalLanguageData)}],
      subtitle: [{value:subtitle ? subtitle : undefined,  disabled: checkIfFieldDisabled('subtitle', fields, originalLanguageData)}],
      title: [{value:title ? title : undefined,  disabled: checkIfFieldDisabled('title', fields, originalLanguageData)}],
      position:[{value: position ? position : undefined,  disabled: checkIfFieldDisabled('position', fields, originalLanguageData)}],
      size:[{value: size ? size : undefined,  disabled: checkIfFieldDisabled('size', fields, originalLanguageData)}],
      name :[{value: name ? name : undefined, disabled: checkIfFieldDisabled('name', fields, originalLanguageData)}],
      fallbackUrl:[{value: fallbackUrl ? fallbackUrl : undefined, disabled: checkIfFieldDisabled('fallbackUrl', fields, originalLanguageData) }],
      filter: [{value:filter ? filter: undefined,  disabled: checkIfFieldDisabled('filter', fields, originalLanguageData)}],
      altTitle:[ {value:altTitle ? altTitle : undefined,  disabled: checkIfFieldDisabled('altTitle', fields, originalLanguageData)}],
      widgetStyle:[{value: widgetStyle ? widgetStyle : undefined, disabled: checkIfFieldDisabled('widgetStyle', fields, originalLanguageData)}],
    })

    this.imageEditForm.valueChanges.subscribe( (values) => {
      if(!originalLanguageData){
        const {title,subtitle, url, linkURL, altTitle} = values
        this.returnChanges.emit({
          type:"image",
          form: this.imageEditForm,
          validForm: this.imageEditForm.valid,
          translations:{title,subtitle, url, linkURL, altTitle},
          saveDisabled: !this.imageEditForm.valid
        })
      }
    })

    this.imageEditForm.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.formChange.emit(this.imageEditForm);
    });

  }

  initWidgetEdit(){
    const {id, type,filter, url, linkURL, source, copyright, subtitle, title,widgetStyle, position, size, name, fallbackUrl , altTitle} = this.imageData
    
    if(url)
      this.supportUrl(url)

    this.imageEditForm = this.fb.group({
      id:[{value:id ? id : undefined, disabled:false}],
      type:[{value:type ? type : undefined, disabled:false}],
      url:[{value:url ? url : undefined, disabled:false}, [Validators.required, Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]],
      linkURL:[{value: linkURL ? linkURL :'', disabled:false},[Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]],
      source:[{value:source ? source : undefined, disabled:false}],
      copyright:[{value: copyright ? copyright : undefined, disabled:false}],
      subtitle:[{value: subtitle ? subtitle : undefined, disabled:false}],
      title:[{value: title ? title : undefined, disabled:false}],
      position:[{value: position ? position : undefined, disabled:false}],
      size:[{value: size ? size : undefined, disabled:false}],
      name:[{value: name ? name : undefined, disabled:false}],
      filter:[{value: filter ? filter :undefined, disabled:false}],
      widgetStyle:[{value: widgetStyle ? widgetStyle : undefined, disabled:false}],
      altTitle:[{value: altTitle ? altTitle : undefined, disabled:false}],
      fallbackUrl:[{value: fallbackUrl ? fallbackUrl : undefined, disabled:false},[Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]]
    })

    this.checkdyanimcContentUrl(url)
    this.setFieldValidator('url', url)
    this.setFieldValidator('linkURL', linkURL)

    this.imageEditForm.get("linkURL")?.valueChanges.subscribe( (newurl) => {
      this.setFieldValidator('linkURL', newurl)
    })

    this.imageEditForm.get("url")?.valueChanges.subscribe( (newurl) => {
      this.setFieldValidator('url', newurl)
      if(this.addUrl){
        this.url = newurl
        this.urlData = './../../assets/img/default.jpg';
        this.supportUrl(newurl)
      }
      this.checkdyanimcContentUrl(newurl)
    })

    this.imageEditForm.valueChanges.subscribe( async (value) => {
      if(value.url){
        this.checkdyanimcContentUrl(value.url)
        this.calculateImgSize(value.size)
      }

      this.returnChanges.emit({
        type:"image",
        form: this.imageEditForm,
        validForm: this.imageEditForm.valid,
        data:value,
        saveDisabled: !this.imageEditForm.valid || !value.url
      })

    })
  }

  setFieldValidator(field, value) {
    if (!value) return;

    if (value.match(/\${(.+)\}/)){
      this.imageEditForm.get(field)?.setValidators([ Validators.pattern(this.urlRegexDynamic)]);
      this.imageEditForm.get(field)?.updateValueAndValidity({ emitEvent: false });
    } else {
      this.checkFieldPattern(field, value)
    }
  }

  buildAnchorUrl(anchor: { id: string }): string {
    if (!anchor || !anchor.id) {
      console.warn('buildAnchorUrl: invalid anchor', anchor);
      return '';
    }
    return `#${anchor.id}`;
  }

  buildChildPageUrl(child: { id: string }): string {
    if (!child || !child.id) {
      console.warn('buildChildPageUrl: invalid child', child);
      return '';
    }
    return child.id === 'main' ? 'dpp://main' : `child:${child.id}`;
  }

  setLinkUrl(value: string): void {
    const controlName = 'linkURL';
    this.imageEditForm.patchValue({
      [controlName]: value
    });

    const ctrl = this.imageEditForm.get(controlName);
    if (ctrl) {
      ctrl.markAsDirty();
      ctrl.markAsTouched();
      ctrl.updateValueAndValidity();
    }
  }

  private makeLinkChip(value: string | null | undefined): LinkChip | null {
    if (!value) return null;
    // dpp://main
    if (value === 'dpp://main') {
      const match = this.childPages?.find(x => x.id === 'main');
      return { kind: 'child', id: 'main', label: match?.name || 'Main' };
    }
    // child:<id>
    if (value.startsWith('child:')) {
      const id = value.slice('child:'.length).trim();
      if (!id) return null;
      const match = this.childPages?.find(x => x.id === id);
      return {
        kind: 'child',
        id,
        label: match?.name || id,
      };
    }
    // #<id>
    if (value.startsWith('#')) {
      const id = value.slice(1).trim();
      if (!id) return null;
      const match = this.anchorOptions?.find(x => x.id === id);
      return {
        kind: 'anchor',
        id,
        label: match?.label || match?.name || id,
      };
    }
    // normal url -> no chip
    return null;
  }

  clearLinkChip(): void {
    this.setLinkUrl('');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkFieldPattern(field, urlToCheck){
    if(urlToCheck){
      try{
        const url = new URL(urlToCheck)
  
        if(url.protocol === "pass:" )
          this.imageEditForm.get(field)?.setValidators([Validators.pattern(this.urlRegexDoc), urlInvalidCharactersValidator()]);

        if(url.protocol === "https:" || url.protocol === "http:" || url.protocol === "tridy:" || url.protocol === "context:")
          this.imageEditForm.get(field)?.setValidators([Validators.pattern(this.urlRegex), urlInvalidCharactersValidator()]);

        this.imageEditForm.updateValueAndValidity()

      }catch(e){}
    }
  }

  async calculateImgSize(size){
    if(size?.length == 0 || !size || (size && size[0] == 0 && size[1] == 0) ){
      await this.uploadService.calculateImgFileSize(this.urlData).then(value => {
        this.imageEditForm.patchValue({size: value})
      })
    }
  }

  readDocument(event: any): void {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const maxSizeInBytes = 10 * 1024 * 1024; // 10 MB size limit
      
      // Define allowed file types
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'image/webp',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/plain',
        'text/csv',
        'text/vcard',
        'application/rtf'
      ];
  
      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        const message = this.translate.instant("SnackBarConstants.INVALID_FILE_FORMAT");
        this.commentService.addSnackBar.emit(message);
        return;
      }
      
      // Validate file size
      if (file.size > maxSizeInBytes) {
        const message = this.translate.instant("SnackBarConstants.FILE_TOO_LARGE");
        this.commentService.addSnackBar.emit(message);
        return;
      }
      
      // Proceed with reading and uploading the file
      const reader = new FileReader();
      this.newImage = file;
      reader.readAsDataURL(file);
      reader.onload = async (e) => {
        if (e.target) {
          const urlNew = e.target.result as string;
          try {
            const uploadURL = await this.uploadService.uploadDocument(this.passId, this.newImage, this.widgetNumber);
            if (uploadURL) {
              this.imageEditForm.get("linkURL")?.setValidators([Validators.pattern(this.urlRegexDoc), urlInvalidCharactersValidator()]);
              this.linkUrlTransform = uploadURL;
              this.imageEditForm.patchValue({ linkURL: this.linkUrlTransform });
            }
          } catch (error) {
            console.error(error);
            const message = this.translate.instant("SnackBarConstants.UPLOAD_IMG_FAILED");
            this.commentService.addSnackBar.emit(message);
          }
        }
      };
    }
  }

  checkdyanimcContentUrl(value){
    if(value?.match(/\${(.+)\}/))
    this.dynamicUrl = true
    else
    this.dynamicUrl = false
  }

  supportUrl(url){
    this.urlData = url
    if(url){
      try{
        const firebaseUrl = supportUrlStructure(url,this.passId, this.settingsService.contextId$)
        if(firebaseUrl){
          this.urlData=firebaseUrl
          this.addUrl = false
        }
      }catch(error){}
    }
  }

  deleteImg(x:any){
    this.url=""
    this.imageEditForm.patchValue({ url:this.url, size:undefined, altTitle: undefined, fallbackUrl: undefined })
    this.urlData=""
  }

  checkNewUrl(){
    this.url = this.imageEditForm.value.url
    this.supportUrl(this.imageEditForm.value.url)
  }
  
  typeURL(){
    this.addUrl = true
    this.urlData = './../../assets/img/default.jpg';
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }


  returnChangesFilters($event){
    this.imageEditForm.patchValue({filter: $event.newFilters})
  }

  returnChangesStyle($event){
    this.imageEditForm.patchValue({widgetStyle: $event.newStyle})
  }

  returnChangesDragFileBrowseUI($event){
    if($event){
      const {returnSupportUrl,returnUrlData, enterUrl} = $event

      this.dragFileData = $event

      if(enterUrl)
        this.typeURL()
      else{
        this.addUrl = false
        this.urlData = undefined
        this.changes= true
        this.noUrl = false;
      }

      if(returnUrlData)
        this.urlData = returnUrlData

      if(returnSupportUrl){
        this.url = returnSupportUrl
        this.imageEditForm.patchValue({ url: returnSupportUrl })
        this.dragFileData.returnSupportUrl = undefined
        this.dragFileData.returnUrlData = undefined
      }
    }
  }
}
